// @ts-strict-ignore
import last from 'lodash/last';

import {findSelectedItems} from 'common/components/items-menu/utils';
import {Placement} from 'common/components/tooltip/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  DisplayContext,
  FilterOperator,
  FilterValue,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {DateRangeType} from 'common/types/reports/bookmark';
import {BookmarkDateRange, TimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getSimilarDateRange} from 'common/widgets/date-range-picker-new/util';
import {getCurrentMoment} from 'common/util/date';
import {
  EntryPart,
  FiltersOperator,
  OperatorOverride,
  QueryFilterEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {PropertyType} from 'common/report/constants';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {pluralize} from 'common/util/string';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {Segmentation} from 'common/types/reports/query';

import {
  isAggregateUserFreqByGroupSegmentation,
  isEventBasedBehavioralSegmentation,
  isRevenueBehaviorSegmentation,
} from '../behavior/utils';
import {PropertyTypeOperatorMap, SelectedOperator} from './constants';

/**
 * Transforms bookmark formatted filters `PropertyFilter[]` to a filter type
 * entry row for `mp-query-entry-section` to render
 *
 * @param filters
 * @param filtersOperator
 */
export function getFilterEntriesFromFilters(
  filters: PropertyFilter[],
  filtersOperator: FiltersOperator,
): QueryFilterEntry[] {
  return filters.map((filter) => ({
    type: SelectorTypeDefinitions.Filter,
    event: filter as Partial<PropertyFilter>,
    filtersOperator: filtersOperator ?? FiltersOperator.And,
    ...(isFirstTimeEventFilter(filter) ? {entryOrder: Number.MAX_SAFE_INTEGER} : {}),
  })) as QueryFilterEntry[];
}

/**
 * Updates the selected filter operator and attemps to preserve
 * any pre-existing selected filter values
 */
export function applyFilterOperatorToFilter(
  filterOperator: FilterOperator,
  currentFilter: PropertyFilter,
): PropertyFilter {
  const filter = currentFilter.setAttribute(`filterOperator`, filterOperator);

  const {filterValue: currentFilterValue, propertyType, propertyObjectKey} = currentFilter;
  const {filterValue: defaultFilterValue, filterOperator: defaultFilterOperator} = filter;

  if (propertyObjectKey?.length) {
    /** @todo: [shawn] Refactor into setAttribute */
    filter.propertyObjectKey = propertyObjectKey;
  }

  // when changing between filter operators, we want to transition to a valid filter state
  // in some cases, when there's not enough information to fully construct a new filter
  // for the selected operator, we omit the validation stage to allow for user input
  let performValueValidation = true;

  // attempts to preserve the currently selected date filter by applying the new selected
  // operator which maps to a DateRangeType
  if (propertyType === PropertyType.Datetime) {
    const oldDateRange = currentFilter.filterValue as DateRange;
    const similarDate = getSimilarDateRange(
      oldDateRange,
      getCurrentMoment({localized: true}),
      filterOperator as DateRangeType,
    );
    const filterValue = similarDate ? currentFilter.createDateRange(similarDate) : (defaultFilterValue as DateRange);
    if (filterValue.isValid() && filterValue.type === filterOperator) {
      filter.filterValue = filterValue;
      filter.filterOperator = filterValue.type;
    }
  } else if (currentFilterValue && (propertyType === PropertyType.Number || propertyType === PropertyType.String)) {
    // attempts to preserve the first selected filter value entry from an array filterValue
    const isCurrentValueArray = Array.isArray(currentFilterValue);
    const operatorRequiresArrayValue = [
      NumberOperator.Between,
      NumberOperator.NotBetween,
      StringOperator.Equals,
      StringOperator.DoesNotEqual,
    ].includes(filterOperator as NumberOperator);

    if (operatorRequiresArrayValue) {
      const isNumericFilter = propertyType === PropertyType.Number;
      performValueValidation = false;
      filter.filterValue = isCurrentValueArray
        ? // keeps selected array as new value
          currentFilterValue
        : // populates a numeric filter [value, value]
          ((isNumericFilter ? [currentFilterValue, null] : [currentFilterValue]) as FilterValue);
    } else {
      // grabs first element from selected value when possible
      filter.filterValue = isCurrentValueArray ? currentFilterValue[0] : currentFilterValue;
    }
  } else {
    filter.filterValue = currentFilterValue;
  }

  // when value preservation logic fails to end in a valid state, we revert
  // to the generated defaults by the property filter class
  if (performValueValidation && filter.isValid() === false) {
    filter.filterOperator = defaultFilterOperator;
    filter.filterValue = defaultFilterValue;
  }

  return filter;
}

export function getListQuantifierSections({listQuantifier}: PropertyFilter): Section[] {
  const items = [
    {
      label: ({t}) => t(`filters.list-operator.any-in-list`),
      value: ListQuantifier.Any,
      tooltip: {text: ({t}) => t(`filters.list-operator.tooltip.any-in-list`)},
      isSelected: listQuantifier === ListQuantifier.Any,
    },
    {
      label: ({t}) => t(`filters.list-operator.all-in-list`),
      value: ListQuantifier.All,
      tooltip: {text: ({t}) => t(`filters.list-operator.tooltip.all-in-list`)},
      isSelected: listQuantifier === ListQuantifier.All,
    },
  ];
  return [{items}];
}

/**
 * Returns SecionItems for all avaialble filter operators
 * @param filter filter instance to fetch operators for
 * @param operatorOverrides if defined, overrides the operators presented to the user for a given filter
 */
export function getFilterOperatorsSections(
  filter: PropertyFilter,
  operatorOverrides: OperatorOverride[] = null,
): Array<Section<SelectedOperator>> {
  const {propertyType, filterOperator, filterValue} = filter;
  const isDateRange = propertyType === PropertyType.Datetime;
  const overrides = operatorOverrides ? operatorOverrides.map(({value}) => value) : null;
  const operators =
    overrides ?? (isDateRange ? filter.createDateRange().getDateRangeTypeOptions() : filter.getFilterOperators());
  const labelMap = new Map(operatorOverrides?.map(({value, label}) => [value, label]));
  const operatorMap = PropertyTypeOperatorMap.get(propertyType);

  const items: Array<SectionItem<SelectedOperator>> = Object.entries(operatorMap)
    .filter(([operator]) => operators.includes(operator as FilterOperator))
    .map(([operator, description]) => {
      return {
        label:
          labelMap.get(operator as FilterOperator) ??
          (({print}) => `${print(description.symbol)}` + (description.label ? ` (${description.label})` : ``)),
        isSelected:
          isDateRange && filterValue
            ? (filterValue as BookmarkDateRange).type === operator
            : filterOperator === operator,
        value: [operator as FilterOperator, description],
        tooltip: description.tooltip,
      };
    });

  return [{items}];
}

export function getSelectedOperator(filter: PropertyFilter) {
  return last(findSelectedItems<SelectedOperator>(getFilterOperatorsSections(filter)))?.value;
}

export function getFilterOperatorTooltip(filter: PropertyFilter) {
  const [, mapping] = getSelectedOperator(filter);
  return mapping?.label && {text: mapping.label, placement: Placement.Bottom};
}

export function getFilterOperatorSymbol(filter: PropertyFilter) {
  const [, mapping] = getSelectedOperator(filter);
  return mapping.symbol;
}

export function getDurationFilterUnitLabel(filter: PropertyFilter) {
  return pluralize(TimeUnitLabels[filter.filterDateUnit ?? TimeUnit.Day], filter.filterValue as number);
}

export function createFilter(rowProps: EntryRowProps<QueryFilterEntry>) {
  const filter = PropertyFilterFactory.createPropertyFilter(rowProps.entry.event, {
    displayContext: DisplayContext.Default,
  });

  const listOfObjectsProperty = rowProps.sectionProps.listProperty;
  if (listOfObjectsProperty) {
    // _parent is non-enumerable so it is dropped when propertyFilter is serialized through attrs (to prevent a
    // JSON.stringify circular reference error)
    filter.setParent(new PropertyFilter(listOfObjectsProperty));
  }

  return filter;
}

export function getNextEntryPart(entry: Segmentation) {
  let entryPart = EntryPart.FilterValue;
  if (isEventBasedBehavioralSegmentation(entry)) {
    if (!entry.behavior.aggregationOperator) {
      entryPart = EntryPart.Aggregator;
    } else if (!entry.behavior.event.value) {
      entryPart = EntryPart.SubValue;
    }
  } else if (isRevenueBehaviorSegmentation(entry) && !entry.behavior.property.value) {
    entryPart = EntryPart.SubValue;
  } else if (isAggregateUserFreqByGroupSegmentation(entry) && !entry.behavior.filters[0].behavior?.property?.value) {
    entryPart = EntryPart.SubValue;
  }
  return entryPart;
}
